import type { FC, ReactNode } from 'react'

import {
    createContext,
    useCallback,
    useContext,
    useMemo,
    useState
} from 'react'
import { CircleCheck, Info, TriangleAlert, X } from 'lucide-react'
import { cn } from '@/lib/utils'

type ToastTone = 'success' | 'error' | 'info'

interface ToastItem {
    id: number
    title: string
    description?: string
    tone: ToastTone
}

interface ToastContextValue {
    toast: (input: { title: string, description?: string, tone?: ToastTone, duration?: number }) => void
}

const ToastContext = createContext<ToastContextValue>({ toast: () => undefined })

let counter = 0

const ICONS = {
    success: CircleCheck,
    error: TriangleAlert,
    info: Info
}

const TONES = {
    success: 'bg-white text-brand-choco',
    error: 'bg-[#FFF1EE] text-brand-choco',
    info: 'bg-white text-brand-choco'
}

const ICON_TONES = {
    success: 'text-brand-green',
    error: 'text-destructive',
    info: 'text-brand-orange'
}

export const ToastProvider: FC<{ children: ReactNode }> = ({ children }): ReactNode => {
    const [items, setItems] = useState<Array<ToastItem>>([])

    const dismiss = useCallback((id: number) => {
        setItems(current => current.filter(item => item.id !== id))
    }, [])

    const toast = useCallback<ToastContextValue['toast']>(({ title, description, tone = 'info', duration = 4200 }) => {
        counter += 1

        const id = counter

        setItems(current => [...current.slice(-2), { id, title, description, tone }])
        window.setTimeout(() => dismiss(id), duration)
    }, [dismiss])

    const value = useMemo(() => ({ toast }), [toast])

    return (
        <ToastContext.Provider value={value}>
            {children}

            <div className='pointer-events-none fixed inset-x-0 top-4 z-[70] flex flex-col items-center gap-2 px-4' aria-live='polite' role='status'>
                {items.map(item => {
                    const Icon = ICONS[item.tone]

                    return (
                        <div
                            key={item.id}
                            className={cn('pointer-events-auto flex w-full max-w-sm animate-pop-in items-start gap-3 rounded-2xl border-2 border-brand-choco p-4 shadow-pop', TONES[item.tone])}
                        >
                            <Icon className={cn('mt-0.5 size-5 shrink-0', ICON_TONES[item.tone])} aria-hidden='true' />
                            <div className='min-w-0 flex-1'>
                                <p className='font-heading text-[15px] font-semibold leading-snug'>
                                    {item.title}
                                </p>
                                {item.description && (
                                    <p className='mt-0.5 text-sm leading-relaxed text-muted-foreground'>
                                        {item.description}
                                    </p>
                                )}
                            </div>
                            <button
                                type='button'
                                onClick={() => dismiss(item.id)}
                                className='-mr-1 -mt-1 inline-flex size-7 items-center justify-center rounded-full text-brand-choco/70 hover:bg-brand-sand hover:text-brand-choco'
                                aria-label='Tutup notifikasi'
                            >
                                <X className='size-4' />
                            </button>
                        </div>
                    )
                })}
            </div>
        </ToastContext.Provider>
    )
}

export const useToast = (): ToastContextValue => useContext(ToastContext)
